/**
 * Gate incidents (Wave D) — the guard's incident log: security, fire, medical,
 * maintenance. Logging one pushes admins + committee instantly; resolved ones
 * stay in the list as the shift record. Admins can open this too (they pass
 * the gate-staff check as supervisors).
 * Wired: GET/POST /api/gate/incidents, POST /api/gate/incidents/<id>/resolve.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, Chip, EmptyState, Field, Input, Pill, Screen,
  SectionLabel, Sheet, Skeleton, TopBar,
} from '@/ui';

const KINDS = ['SECURITY', 'FIRE', 'MEDICAL', 'MAINTENANCE', 'OTHER'] as const;
const KIND_LABEL: Record<string, DictKey> = {
  SECURITY: 'inc_kind_security', FIRE: 'inc_kind_fire', MEDICAL: 'inc_kind_medical',
  MAINTENANCE: 'inc_kind_maintenance', OTHER: 'inc_kind_other',
};

type Row = { id: string; kind: string; note: string; resolved: boolean; by: string; when: string };

function toRow(v: unknown, i: number): Row {
  const iso = str(v, 'createdAt');
  const d = iso ? new Date(iso) : null;
  let when = '';
  if (d && !Number.isNaN(d.getTime())) {
    when = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      + ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  }
  return {
    id: str(v, 'id') ?? `i${i}`,
    kind: (str(v, 'kind') ?? 'OTHER').toUpperCase(),
    note: str(v, 'note') ?? '',
    resolved: Boolean((v as { resolved?: boolean })?.resolved),
    by: str(v, 'reportedByName') ?? 'Gate desk',
    when,
  };
}

export default function GuardIncidents() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/gate/incidents', 'items');
  const rows = items.map(toRow);

  const [open, setOpen] = React.useState(false);
  const [kind, setKind] = React.useState<string>('SECURITY');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const submit = () => {
    if (busy) return;
    if (note.trim().length < 3) { setNotice(t('incidents_need_note')); return; }
    setBusy(true);
    api.post<unknown>('/api/gate/incidents', { kind, note: note.trim() })
      .then(() => { setNotice(t('incidents_logged_ok')); void reload(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('incidents_log_error')))
      .finally(() => { setBusy(false); setOpen(false); setNote(''); });
  };

  const resolve = (row: Row) => {
    api.post<unknown>(`/api/gate/incidents/${row.id}/resolve`, {})
      .then(() => { setNotice(t('incidents_resolved_ok')); void reload(); })
      .catch(() => setNotice(t('incidents_resolve_error')));
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('incidents_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <Button title={t('incidents_log')} onPress={() => { setKind('SECURITY'); setNote(''); setNotice(null); setOpen(true); }} />
        {notice ? (
          <AppText variant="bodySm" weight="500" style={{ marginTop: space.s3, color: colors.textPrimary }}>
            {notice}
          </AppText>
        ) : null}
        <View style={{ marginTop: space.s4 }}>
          {state === 'loading' ? (
            <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={76} />)}</View>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="shield" size={40} color={colors.textTertiary} />}
              title={t('incidents_empty_title')}
              body={t('incidents_empty_body')}
            />
          ) : (
            <View style={{ gap: space.s3 }}>
              {rows.map((r) => (
                <Card key={r.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                    <Pill status={r.resolved ? 'approved' : 'urgent'} label={r.resolved ? 'RESOLVED' : (KIND_LABEL[r.kind] ? t(KIND_LABEL[r.kind]) : r.kind)} />
                    <View style={{ flex: 1 }} />
                    <AppText variant="bodySm" tone="tertiary">{r.when}</AppText>
                  </View>
                  <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>{r.note}</AppText>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: space.s2 }}>
                    <AppText variant="bodySm" tone="tertiary" style={{ flex: 1 }}>{t('incidents_logged_by', { name: r.by })}</AppText>
                    {!r.resolved ? (
                      <AppText variant="bodySm" weight="600" tone="brand" onPress={() => resolve(r)}>
                        {t('incidents_mark_resolved')}
                      </AppText>
                    ) : null}
                  </View>
                </Card>
              ))}
            </View>
          )}
        </View>
      </Screen>

      <Sheet visible={open} onClose={() => setOpen(false)}>
        <SectionLabel>{t('incidents_log')}</SectionLabel>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          {t('incidents_sheet_sub')}
        </AppText>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s3 }}>
          {KINDS.map((k) => (
            <Chip key={k} label={t(KIND_LABEL[k])} active={kind === k} onPress={() => setKind(k)} />
          ))}
        </View>
        <View style={{ marginTop: space.s4 }}>
          <Field label={t('incidents_field')}>
            <Input value={note} onChangeText={setNote} multiline maxLength={500}
              placeholder={t('incidents_placeholder')} />
          </Field>
        </View>
        <Button title={busy ? t('incidents_logging') : t('incidents_log_submit')} disabled={busy}
          style={{ marginTop: space.s4 }} onPress={submit} />
      </Sheet>
    </Screen>
  );
}
